import { useCallback, useEffect, useRef } from 'react';
import { useTrail, animated } from '@react-spring/web';
import { Users } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useOrgMembers,
  useOrgRoles,
  useAssignMemberRole,
  useDeactivateOrgMember,
  useReactivateOrgMember,
} from '@/hooks/org-admin';
import { MemberRow } from '@/components/molecules/org-admin/MemberRow';
import { grantableRolesFor } from '@/components/molecules/org-admin/roleOptions';
import type { OrgAdminSelfContext } from '@/components/molecules/org-admin/types';

export interface MembersPanelProps {
  self: OrgAdminSelfContext;
}

const SKELETON_ROWS = [{ email: 'w-40' }, { email: 'w-32' }, { email: 'w-48' }, { email: 'w-36' }] as const;

function SkeletonMemberRow({ emailWidth, isLast }: { emailWidth: string; isLast: boolean }) {
  return (
    <div className={`flex items-center justify-between gap-4 px-4 py-3 ${!isLast ? 'border-b border-border/60' : ''}`}>
      <div className="min-w-0 flex-1">
        <div className={`h-3.5 rounded-md bg-muted-foreground/10 loading-parallax ${emailWidth}`} />
        <div className="mt-2 h-5 w-16 rounded-full bg-muted-foreground/[0.07] loading-parallax" />
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <div className="h-8 w-32 rounded-md bg-muted-foreground/[0.07] loading-parallax" />
        <div className="h-8 w-40 rounded-md bg-muted-foreground/[0.07] loading-parallax" />
        <div className="h-8 w-8 rounded-md bg-muted-foreground/[0.07] loading-parallax" />
      </div>
    </div>
  );
}

export function MembersPanel({ self }: MembersPanelProps) {
  const { data: members = [], isLoading } = useOrgMembers();
  const { data: roles = [] } = useOrgRoles();
  const assignableRoles = grantableRolesFor(self.currentUserRole, roles);
  const assignRole = useAssignMemberRole();
  const deactivate = useDeactivateOrgMember();
  const reactivate = useReactivateOrgMember();

  const hasAnimatedRef = useRef(false);
  useEffect(() => {
    if (!isLoading) hasAnimatedRef.current = true;
  }, [isLoading]);

  const trail = useTrail(members.length, {
    from: { opacity: 0, transform: 'translateY(6px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 320, friction: 28 },
    immediate: hasAnimatedRef.current,
  });

  const skeletonTrail = useTrail(SKELETON_ROWS.length, {
    from: { opacity: 0, transform: 'translateY(6px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 320, friction: 28 },
  });

  const handleAssignRole = useCallback(
    (memberId: string, roleId: string) => {
      assignRole.mutate(
        { memberId, input: { role_id: roleId } },
        {
          onSuccess: () => toast.success('Role updated'),
          onError: (err) => toast.error((err as Error).message || 'Failed to update role'),
        },
      );
    },
    [assignRole],
  );

  const handleToggleActive = useCallback(
    (userId: string, status: string) => {
      const isActive = status.toLowerCase() === 'active';
      const mutation = isActive ? deactivate : reactivate;
      mutation.mutate(userId, {
        onSuccess: () => toast.success(isActive ? 'Member deactivated' : 'Member reactivated'),
        onError: (err) => toast.error((err as Error).message || 'Failed to update member'),
      });
    },
    [deactivate, reactivate],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          skeletonTrail.map((style, index) => (
            <animated.div key={index} style={style}>
              <SkeletonMemberRow
                emailWidth={SKELETON_ROWS[index].email}
                isLast={index === SKELETON_ROWS.length - 1}
              />
            </animated.div>
          ))
        ) : members.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center text-sm text-muted-foreground">
            <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Users className="h-7 w-7 text-primary" />
            </div>
            <p>No members yet.</p>
          </div>
        ) : (
          trail.map((style, index) => {
            const member = members[index];
            const isAssigningThis = assignRole.isPending && assignRole.variables?.memberId === member.id;
            const isTogglingThis =
              (deactivate.isPending && deactivate.variables === member.user_id) ||
              (reactivate.isPending && reactivate.variables === member.user_id);

            return (
              <animated.div key={member.id} style={style}>
                <MemberRow
                  memberId={member.id}
                  userId={member.user_id}
                  email={member.email}
                  managementLevel={member.management_level}
                  status={member.status}
                  roleId={member.role_id}
                  roleName={member.role_name}
                  isSelf={member.user_id === self.currentUserId}
                  roles={assignableRoles}
                  isAssigningRole={isAssigningThis}
                  isTogglingActive={isTogglingThis}
                  onAssignRole={(roleId) => handleAssignRole(member.id, roleId)}
                  onToggleActive={() => handleToggleActive(member.user_id, member.status)}
                />
              </animated.div>
            );
          })
        )}
      </div>
    </div>
  );
}
